import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NotificationResponse } from '@core/generated/openapi';
import { USER_ROLES } from '@core/constants/application.constants';
import {
    AdminRoutes,
    ModuleRoutes,
} from '@core/constants/module-history.constants';
import { AuthService } from '@services/auth/auth.service';

export interface NotificationTextSegment {
    text: string;
    /** Router link for the entity's Admin details screen, when linked. */
    link?: string[];
}

const RELATED_ENTITY_ROUTES: Record<
    NotificationResponse.RelatedEntityTypeEnum,
    { route: AdminRoutes; adminOnly: boolean }
> = {
    // Payers aren't permission-restricted, so every user gets the link.
    ACCOUNT_PAYOR: {
        route: AdminRoutes.Payers,
        adminOnly: false,
    },
    // Admin > Locations and Admin > Providers are Admin role only.
    ACCOUNT_LOCATION: {
        route: AdminRoutes.Locations,
        adminOnly: true,
    },
    ACCOUNT_PROVIDER: {
        route: AdminRoutes.Providers,
        adminOnly: true,
    },
};

/**
 * Splits a notification's description so the payer, location or provider name
 * in it can be shown as a link to that entity's Admin details screen.
 *
 * The backend saves the name (relatedEntityLabel) and where it starts in the
 * description (relatedEntityLabelStart) when the notification is created. The
 * link only applies when the description has exactly that name at that
 * position; otherwise, and for notifications without a related entity, the
 * description is returned as plain text.
 */
export function getNotificationTextSegments(
    notification: NotificationResponse,
    isAdmin: boolean,
): NotificationTextSegment[] {
    const description = notification?.description ?? '';
    const plain = [{ text: description }];

    const {
        relatedEntityType: type,
        relatedEntityId: id,
        relatedEntityLabel: label,
        relatedEntityLabelStart: start,
    } = notification ?? {};
    const target = type ? RELATED_ENTITY_ROUTES[type] : undefined;

    if (!target || id == null || !label || start == null || start < 0) {
        return plain;
    }
    if (target.adminOnly && !isAdmin) {
        return plain;
    }
    if (description.substring(start, start + label.length) !== label) {
        return plain;
    }

    return [
        { text: description.substring(0, start) },
        {
            text: label,
            link: ['/', ModuleRoutes.Admin, target.route, String(id)],
        },
        { text: description.substring(start + label.length) },
    ].filter((segment) => segment.text !== '');
}

/**
 * A notification's description, with the payer, location or provider name it
 * mentions linked to that entity's Admin details screen (OSP-2860).
 */
@Component({
    selector: 'rcp-oup-notification-description',
    templateUrl: './notification-description.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink],
})
export class NotificationDescriptionComponent implements OnChanges {
    @Input() notification: NotificationResponse;

    /** Emitted when the link is clicked, e.g. to close the bell or a dialog. */
    @Output() linkClicked = new EventEmitter<void>();

    segments: NotificationTextSegment[] = [];

    constructor(private authService: AuthService) {}

    ngOnChanges(): void {
        const user = this.authService.getCurrentUser()?.value;
        this.segments = getNotificationTextSegments(
            this.notification,
            user?.primaryRole?.id === USER_ROLES.ADMIN.id,
        );
    }

    // Notification rows open the notification dialog when clicked, so keep the
    // click on the link from reaching them. The router link still navigates.
    onLinkClick(event: MouseEvent): void {
        event.stopPropagation();
        this.linkClicked.emit();
    }
}
